import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Receipt, Plus, Search, Printer, X, Check, Trash2, ArrowRight, Eye,
  DollarSign, Shield, AlertCircle, FileText, Download, QrCode
} from 'lucide-react';
import { useWorkflowStore } from '../store/workflowStore';
import { toast } from '../components/Toast';
import type { WorkflowInvoice, InvoicePayment, PaymentStatus } from '../types/workflow';

const fmtCurrency = (n: number) =>
  `Rs. ${n.toLocaleString('en-LK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (d: string) => d ? new Date(d).toLocaleDateString('en-LK', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const uid = () => `pay-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const PAY_STATUS_COLORS: Record<PaymentStatus, string> = {
  unpaid:  'bg-red-100 text-red-700',
  partial: 'bg-yellow-100 text-yellow-700',
  paid:    'bg-emerald-100 text-emerald-700',
};

export default function InvoicesPage() {
  const navigate = useNavigate();
  const { invoices, updateInvoice, addPaymentToInvoice, markInvoiceComplete, warranties } = useWorkflowStore();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<PaymentStatus | 'all'>('all');
  const [showDetail, setShowDetail] = useState<WorkflowInvoice | null>(null);
  const [showPayment, setShowPayment] = useState<string | null>(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<InvoicePayment['method']>('cash');
  const [payRef, setPayRef] = useState('');
  const [payError, setPayError] = useState('');

  const filtered = useMemo(() => invoices.filter(inv => {
    const matchSearch =
      inv.customerName.toLowerCase().includes(search.toLowerCase()) ||
      inv.invoiceNumber.toLowerCase().includes(search.toLowerCase());
    const matchStatus = statusFilter === 'all' || inv.paymentStatus === statusFilter;
    return matchSearch && matchStatus;
  }), [invoices, search, statusFilter]);

  const handleAddPayment = () => {
    if (!showPayment) return;
    const inv = invoices.find(i => i.id === showPayment);
    if (!inv) return;
    const amt = parseFloat(payAmount);
    if (!amt || amt <= 0) { setPayError('Enter a valid amount.'); return; }
    if (amt > inv.balance) { setPayError(`Exceeds balance of ${fmtCurrency(inv.balance)}`); return; }
    addPaymentToInvoice(showPayment, { date: new Date().toISOString(), amount: amt, method: payMethod, reference: payRef });
    toast.success('Payment Recorded', `${fmtCurrency(amt)} added.`);
    setShowPayment(null);
    setPayAmount('');
    setPayRef('');
    setPayError('');
    // refresh detail
    const updated = invoices.find(i => i.id === showDetail?.id);
    if (updated) setShowDetail(updated);
  };

  const handleMarkComplete = (id: string) => {
    markInvoiceComplete(id);
    toast.success('Completed', 'Invoice marked as completed. Warranty created automatically.');
    const updated = invoices.find(i => i.id === id);
    if (updated) setShowDetail(updated);
  };

  const handlePrint = (inv: WorkflowInvoice) => {
    navigate(`/admin/invoices/${inv.id}/print`);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Invoices</h1>
          <p className="text-sm text-gray-500 mt-0.5">Manage invoices and payments</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => navigate('/admin/quotations')}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm font-medium hover:bg-gray-50"
          >
            <FileText className="w-4 h-4" /> Convert from Quotation
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: 'Total Invoices', value: invoices.length, color: 'text-blue-600', bg: 'bg-blue-50' },
          { label: 'Unpaid', value: invoices.filter(i => i.paymentStatus === 'unpaid').length, color: 'text-red-600', bg: 'bg-red-50' },
          { label: 'Paid', value: invoices.filter(i => i.paymentStatus === 'paid').length, color: 'text-emerald-600', bg: 'bg-emerald-50' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4">
            <p className={`text-2xl font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30"
            placeholder="Search invoices…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="flex gap-2">
          {(['all','unpaid','partial','paid'] as const).map(s => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`px-3 py-1.5 text-xs rounded-lg font-medium capitalize transition-colors ${statusFilter === s ? 'bg-gray-900 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
            >{s === 'all' ? 'All' : s}</button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-gray-400">
            <Receipt className="w-12 h-12 mb-3 opacity-30" />
            <p className="text-sm">No invoices yet — convert an approved quotation to create one.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Invoice #</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Customer</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Total</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Paid</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Balance</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(inv => (
                  <tr key={inv.id} className="hover:bg-gray-50/50">
                    <td className="px-4 py-3 font-mono text-xs text-gray-700">{inv.invoiceNumber}</td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-gray-900">{inv.customerName}</div>
                      <div className="text-xs text-gray-500">{inv.customerPhone}</div>
                    </td>
                    <td className="px-4 py-3 font-semibold">{fmtCurrency(inv.grandTotal)}</td>
                    <td className="px-4 py-3 text-emerald-600 font-medium">{fmtCurrency(inv.totalPaid)}</td>
                    <td className="px-4 py-3 text-red-600 font-medium">{fmtCurrency(inv.balance)}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{fmtDate(inv.invoiceDate)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium capitalize ${PAY_STATUS_COLORS[inv.paymentStatus]}`}>
                        {inv.paymentStatus}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <button onClick={() => setShowDetail(inv)} title="View Invoice" className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-blue-600 hover:bg-blue-50">
                          <Eye className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handlePrint(inv)} title="Print Invoice" className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100">
                          <Printer className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handlePrint(inv)} title="Download PDF" className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-[#7FA86A] hover:bg-[#F1F6EE]">
                          <Download className="w-3.5 h-3.5" />
                        </button>
                        {inv.paymentStatus !== 'paid' && (
                          <button onClick={() => { setShowPayment(inv.id); setPayAmount(String(inv.balance)); }} title="Add Payment" className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-emerald-600 hover:bg-emerald-50">
                            <DollarSign className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {!inv.warrantyId && (
                          <button onClick={() => handleMarkComplete(inv.id)} title="Mark Complete & Create Warranty" className="w-7 h-7 flex items-center justify-center rounded-lg text-gray-400 hover:text-purple-600 hover:bg-purple-50">
                            <Shield className="w-3.5 h-3.5" />
                          </button>
                        )}
                        {inv.warrantyId && (
                          <button onClick={() => {
                            const w = warranties.find(wr => wr.id === inv.warrantyId);
                            if (w) window.open(`/warranty/verify/${w.warrantyId}`, '_blank');
                          }} title="View Warranty QR" className="w-7 h-7 flex items-center justify-center rounded-lg text-purple-500 hover:text-purple-700 hover:bg-purple-50">
                            <Shield className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* INVOICE DETAIL DRAWER */}
      <AnimatePresence>
        {showDetail && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/40 z-40" onClick={() => setShowDetail(null)} />
            <motion.div
              initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed right-0 top-0 h-full w-full max-w-xl bg-white z-50 overflow-y-auto shadow-2xl"
            >
              <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
                <div>
                  <h2 className="font-semibold text-gray-900">Invoice Details</h2>
                  <p className="text-xs font-mono text-gray-500">{showDetail.invoiceNumber}</p>
                </div>
                <button onClick={() => setShowDetail(null)} className="p-2 rounded-lg hover:bg-gray-100">
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="p-6 space-y-5">
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${PAY_STATUS_COLORS[showDetail.paymentStatus]}`}>
                    {showDetail.paymentStatus}
                  </span>
                  <div className="flex gap-2">
                    <button onClick={() => handlePrint(showDetail)} title="Print" className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs hover:bg-gray-200">
                      <Printer className="w-3.5 h-3.5" /> Print
                    </button>
                    <button onClick={() => handlePrint(showDetail)} title="Download PDF" className="flex items-center gap-2 px-3 py-1.5 bg-[#F1F6EE] text-[#4A6741] rounded-lg text-xs hover:bg-[#D8E7D1]">
                      <Download className="w-3.5 h-3.5" /> Download PDF
                    </button>
                    {showDetail.warrantyId && (() => {
                      const w = warranties.find(wr => wr.id === showDetail.warrantyId);
                      return w ? (
                        <button onClick={() => window.open(`/warranty/verify/${w.warrantyId}`, '_blank')} className="flex items-center gap-2 px-3 py-1.5 bg-purple-50 text-purple-700 rounded-lg text-xs hover:bg-purple-100">
                          <Shield className="w-3.5 h-3.5" /> Warranty QR
                        </button>
                      ) : null;
                    })()}
                  </div>
                </div>
                <div className="bg-gray-50 rounded-xl p-4">
                  <p className="text-xs text-gray-500 mb-1">Customer</p>
                  <p className="font-semibold">{showDetail.customerName}</p>
                  <p className="text-sm text-gray-600">{showDetail.customerPhone}</p>
                  {showDetail.customerAddress && <p className="text-sm text-gray-500">{showDetail.customerAddress}</p>}
                </div>
                {showDetail.quotationId && (
                  <div className="text-xs text-gray-400 bg-blue-50 rounded-lg px-3 py-2">
                    Converted from Quotation ID: {showDetail.quotationId}
                  </div>
                )}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Line Items</p>
                  {showDetail.lineItems.map(li => (
                    <div key={li.id} className="flex justify-between py-2 border-b border-gray-100 text-sm">
                      <div>
                        <span className="font-medium">{li.name}</span>
                        {li.modelNumber && <span className="text-gray-400 ml-2 text-xs">({li.modelNumber})</span>}
                        {li.warrantyPeriod && <span className="text-purple-400 ml-2 text-xs">🛡 {li.warrantyPeriod}</span>}
                        <div className="text-xs text-gray-400">{li.quantity} × {fmtCurrency(li.unitPrice)}</div>
                      </div>
                      <span className="font-medium">{fmtCurrency(li.lineTotal)}</span>
                    </div>
                  ))}
                </div>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{fmtCurrency(showDetail.subtotal)}</span></div>
                  {showDetail.installationCost > 0 && <div className="flex justify-between"><span className="text-gray-500">Installation</span><span>{fmtCurrency(showDetail.installationCost)}</span></div>}
                  {showDetail.labourCost > 0 && <div className="flex justify-between"><span className="text-gray-500">Labour</span><span>{fmtCurrency(showDetail.labourCost)}</span></div>}
                  {showDetail.taxRate > 0 && <div className="flex justify-between"><span className="text-gray-500">Tax ({showDetail.taxRate}%)</span><span>{fmtCurrency(showDetail.taxAmount)}</span></div>}
                  <div className="flex justify-between font-bold text-base pt-2 border-t border-gray-100">
                    <span>Grand Total</span><span className="text-[#7FA86A]">{fmtCurrency(showDetail.grandTotal)}</span>
                  </div>
                  <div className="flex justify-between text-emerald-600 font-medium">
                    <span>Paid</span><span>{fmtCurrency(showDetail.totalPaid)}</span>
                  </div>
                  <div className="flex justify-between text-red-600 font-bold">
                    <span>Balance Due</span><span>{fmtCurrency(showDetail.balance)}</span>
                  </div>
                </div>
                {/* Payment History */}
                {showDetail.payments.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Payment History</p>
                    {showDetail.payments.map(p => (
                      <div key={p.id} className="flex justify-between py-2 border-b border-gray-100 text-sm">
                        <div>
                          <span className="capitalize">{p.method.replace('_', ' ')}</span>
                          {p.reference && <span className="text-gray-400 ml-2 text-xs">{p.reference}</span>}
                          <div className="text-xs text-gray-400">{fmtDate(p.date)}</div>
                        </div>
                        <span className="text-emerald-600 font-medium">{fmtCurrency(p.amount)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {/* Terms */}
                {showDetail.productWarranty && (
                  <div className="bg-purple-50 rounded-xl p-3 text-xs text-purple-700">
                    <p className="font-semibold mb-1">🛡 Warranty</p>
                    <p>{showDetail.productWarranty}</p>
                    {showDetail.serviceWarranty && <p className="mt-1">{showDetail.serviceWarranty}</p>}
                  </div>
                )}
                {/* Linked warranty */}
                {showDetail.warrantyId && (
                  <div className="bg-emerald-50 rounded-xl p-3">
                    <p className="text-xs font-semibold text-emerald-700 mb-1">✅ Warranty Created</p>
                    <button
                      onClick={() => {
                        const w = warranties.find(w => w.id === showDetail.warrantyId);
                        if (w) window.open(`/warranty/verify/${w.warrantyId}`, '_blank');
                      }}
                      className="text-xs text-emerald-600 underline"
                    >View Public Warranty Page</button>
                  </div>
                )}
                {/* Action buttons */}
                <div className="flex flex-wrap gap-3 pt-2">
                  {showDetail.paymentStatus !== 'paid' && (
                    <button onClick={() => { setShowPayment(showDetail.id); setPayAmount(String(showDetail.balance)); }} className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-xl text-sm hover:bg-emerald-100">
                      <DollarSign className="w-4 h-4" /> Add Payment
                    </button>
                  )}
                  {!showDetail.warrantyId && (
                    <button onClick={() => handleMarkComplete(showDetail.id)} className="flex items-center gap-2 px-4 py-2 bg-purple-50 text-purple-700 rounded-xl text-sm hover:bg-purple-100">
                      <Shield className="w-4 h-4" /> Mark Complete + Create Warranty
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* PAYMENT MODAL */}
      <AnimatePresence>
        {showPayment && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowPayment(null)} />
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
              className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-white rounded-2xl z-50 shadow-2xl p-6"
            >
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-semibold text-gray-900">Record Payment</h3>
                <button onClick={() => setShowPayment(null)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-4 h-4" /></button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Amount (Rs.) *</label>
                  <input
                    type="number" min={0} step={0.01}
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[#7FA86A]/30"
                    value={payAmount}
                    onChange={e => { setPayAmount(e.target.value); setPayError(''); }}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Payment Method</label>
                  <select
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none bg-white"
                    value={payMethod}
                    onChange={e => setPayMethod(e.target.value as InvoicePayment['method'])}
                  >
                    <option value="cash">Cash</option>
                    <option value="bank_transfer">Bank Transfer</option>
                    <option value="card">Card</option>
                    <option value="cheque">Cheque</option>
                    <option value="online">Online Payment</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 mb-1 block">Reference (Optional)</label>
                  <input
                    className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none"
                    placeholder="Transaction ID, cheque number…"
                    value={payRef}
                    onChange={e => setPayRef(e.target.value)}
                  />
                </div>
                {payError && <p className="text-xs text-red-500">{payError}</p>}
                <button
                  onClick={handleAddPayment}
                  className="w-full py-3 bg-[#7FA86A] text-white rounded-xl text-sm font-medium hover:bg-[#658C53] transition-colors"
                >
                  Record Payment
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
